import { useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { XIcon } from 'lucide-react';
import { contractDocumentUrlSchema } from '@duke-hq/svc-api/admin/trpc/routers/workPlacements/placements/schemas';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { SelectEmployer, type SelectedEmployer } from '../components/SelectEmployer/SelectEmployer';
import { AddEmployerInline } from '../components/SelectEmployer/AddEmployerInline';
import { EmployerContactSelect } from '../components/EmployerContactSelect/EmployerContactSelect';
import type { NewContactFormHandle } from '../components/EmployerContactSelect/NewContactForm';
import {
  noContactChoice,
  toPlacementContact,
  type EmployerContactChoice,
} from '../components/EmployerContactSelect/contactChoice';
import { isContractOutOfDate } from './contractOutOfDate';
import { testIds } from './testIds';

function setUpContractFormSchema() {
  return z.object({
    employer: z.custom<SelectedEmployer | null>().transform((employer, ctx) => {
      if (employer == null) {
        ctx.addIssue({ code: 'custom', message: t`Choose the employer taking the student` });
        return z.NEVER;
      }
      return employer;
    }),
    contact: z.custom<EmployerContactChoice>().transform((contact, ctx) => {
      if (contact.mode === 'none') {
        ctx.addIssue({ code: 'custom', message: t`Choose a contact or add a new one` });
        return z.NEVER;
      }
      return contact;
    }),
    documentUrl: contractDocumentUrlSchema({
      missing: t`Add the link to the contract document`,
      notWebLink: t`Enter a web link starting with http:// or https://`,
    }),
  });
}

type SetUpContractFormInput = z.input<ReturnType<typeof setUpContractFormSchema>>;
type SetUpContractFormValues = z.output<ReturnType<typeof setUpContractFormSchema>>;

const emptyForm: SetUpContractFormInput = {
  employer: null,
  contact: noContactChoice,
  documentUrl: '',
};

function ModalTitle(): React.ReactElement {
  return (
    <Text fontSize="lg" fontWeight="semibold" w="full">
      <Trans>Create contract</Trans>
    </Text>
  );
}

function ModalBody({
  journeyId,
  courseId,
  onClose,
  onSuccess,
}: {
  journeyId: string;
  courseId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}): React.ReactElement {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const [addingEmployer, setAddingEmployer] = useState(false);
  const newContactRef = useRef<NewContactFormHandle>(null);
  const submitInFlight = useRef(false);

  const resolver = useMemo(() => zodResolver(setUpContractFormSchema()), []);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitted, isSubmitting },
  } = useForm<SetUpContractFormInput, unknown, SetUpContractFormValues>({
    resolver,
    defaultValues: emptyForm,
  });

  const employer = watch('employer');

  const create = trpcAdmin.workPlacements.placements.createPlacementContract.useMutation({
    onSuccess: (_result, input) => {
      toast({ status: 'success', title: t`Contract created` });
      if ('newContact' in input.contact) {
        void utils.workPlacements.employers.getEmployerDetail.invalidate({
          employerId: input.employerId,
        });
        void utils.workPlacements.employers.listEmployers.invalidate();
      }
      onSuccess();
      onClose();
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to create contract`, description: error.message });
      if (isContractOutOfDate(error)) {
        onSuccess();
        onClose();
      }
    },
    onSettled: () => {
      submitInFlight.current = false;
    },
  });

  const chooseEmployer = (next: SelectedEmployer) => {
    if (next.id !== getValues('employer')?.id) {
      setValue('contact', noContactChoice, { shouldValidate: isSubmitted });
    }
    setValue('employer', next, { shouldValidate: isSubmitted });
  };

  const submit = async () => {
    if (submitInFlight.current) {
      return;
    }
    submitInFlight.current = true;
    const newContactValid =
      getValues('contact').mode !== 'new' || (await newContactRef.current?.validate()) === true;
    await handleSubmit(
      (values) => {
        if (!newContactValid) {
          submitInFlight.current = false;
          return;
        }
        create.mutate({
          journeyId,
          employerId: values.employer.id,
          contact: toPlacementContact(values.contact),
          documentUrl: values.documentUrl,
        });
      },
      () => {
        submitInFlight.current = false;
      },
    )();
  };

  if (addingEmployer && courseId != null) {
    return (
      <FormMainContainer>
        <Box px={6} pt={6} w="full">
          <ModalTitle />
        </Box>
        <AddEmployerInline
          courseId={courseId}
          onAdded={(createdEmployer) => {
            chooseEmployer(createdEmployer);
            setAddingEmployer(false);
          }}
          onCancel={() => setAddingEmployer(false)}
        />
      </FormMainContainer>
    );
  }

  return (
    <Box
      as="form"
      w="full"
      noValidate
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        void submit();
      }}
    >
      <FormMainContainer>
        <FormContentContainer>
          <ModalTitle />

          <FormControl isRequired isInvalid={errors.employer != null}>
            <FormLabel>
              <Trans>Employer</Trans>
            </FormLabel>
            <SelectEmployer
              selected={employer}
              onSelect={chooseEmployer}
              onAddNew={courseId != null ? () => setAddingEmployer(true) : undefined}
            />
            <FormErrorMessage>{errors.employer?.message}</FormErrorMessage>
          </FormControl>

          <Controller
            control={control}
            name="contact"
            render={({ field, fieldState }) => (
              <EmployerContactSelect
                employerId={employer?.id ?? null}
                value={field.value}
                onChange={field.onChange}
                newContactRef={newContactRef}
                isRequired
                errorMessage={fieldState.error?.message}
              />
            )}
          />

          <FormControl isRequired isInvalid={errors.documentUrl != null}>
            <FormLabel>
              <Trans>Document link</Trans>
            </FormLabel>
            <Input
              type="url"
              data-testid={testIds.contractDocumentUrlInput}
              placeholder={t`Paste the link all three parties sign`}
              {...register('documentUrl')}
            />
            <FormErrorMessage>{errors.documentUrl?.message}</FormErrorMessage>
          </FormControl>
        </FormContentContainer>

        <FormFooterContainer>
          <Button
            type="submit"
            isLoading={create.isPending || isSubmitting}
            data-testid={testIds.createContractButton}
          >
            <Trans>Create contract</Trans>
          </Button>
          <Button variant="tertiary" onClick={onClose} leftIcon={<XIcon />}>
            <Trans>Cancel</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    </Box>
  );
}

export function SetUpContractModal({
  isOpen,
  onClose,
  journeyId,
  courseId,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  journeyId: string;
  courseId: string | null;
  onSuccess: () => void;
}): React.ReactElement {
  return (
    <FormModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      modalContentHeight="auto"
      modalContent={
        <ModalBody
          journeyId={journeyId}
          courseId={courseId}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      }
    />
  );
}
